/**
 * 평가 타입 정의
 * - work-commitment    : 업무서약서
 * - self-evaluation    : 자기평가
 * - first-evaluation   : 1차평가
 * - second-evaluation  : 2차평가
 * - project-evaluation : 프로젝트평가
 */
export type EvaluationType =
  | "work-commitment"
  | "self-evaluation"
  | "first-evaluation"
  | "second-evaluation"
  | "project-evaluation";

/**
 * 페이지 타입 정의
 * - dashboard  : 대시보드 페이지
 * - evaluation : 평가 작성 페이지
 */
export type PageType = 'dashboard' | 'evaluation';

/**
 * 통계 카드 Props 인터페이스
 */
export interface StatsCardProps {
  title     : string;
  completed : number;
  total     : number;
  deadline  : string;
}

/**
 * 평가 주기 데이터 인터페이스
 */
export interface EvaluationCycle {
  evlYear    : number;        // 평가 연도
  hrEvlStDt  : string;        // 인사평가 시작일
  slfEvlEdDt : string;        // 자기평가 종료일
  eval1EdDt  : string;        // 1차평가 종료일
  eval2EdDt  : string;        // 2차평가 종료일
  pjtEvlEdDt : string;        // 프로젝트평가 종료일
  bizPlgStDt : string;        // 업무서약서 시작일
  bizPlgEdDt : string;        // 업무서약서 종료일
}

/**
 * 프로젝트 평가 정보
 */
export interface ProjectEvaluation {
  evlId               : string; // 프로젝트 평가 ID
  evlType             : string; // 프로젝트평가구분 (팀원, PM 등)
  pjCd                : string; // 프로젝트 코드
  pjNm                : string; // 프로젝트명
  customerName?       : string; // 고객사명 (선택적)
  isCompleted         : string; // 완료 여부 ('Y' | 'N')
  status              : string; // 상태 ('completed' | 'in-progress' | 'waiting')
  startDate?          : string; // 프로젝트 평가 시작일
  endDate?            : string; // 프로젝트 평가 종료일
  evaluatorId?        : string; // 프로젝트 평가자 ID (선택적)
  evaluatorName?      : string; // 프로젝트 평가자 이름 (선택적)
  evaluatorPosition?  : string; // 프로젝트 평가자 직급 (선택적)
  evaluatorDepartment?: string; // 프로젝트 평가자 부서 (선택적)
}

/**
 * 평가 기간 정보
 */
export interface EvaluationPeriod {
  startDate : string;   // 평가 시작일
  endDate   : string;   // 평가 종료일
}

/**
 * 직원 및 평가 정보
 */
export interface Employee {
  evlId             : string; // 평가 대상자 ID
  empNo             : string; // 피평가자 사원번호
  name              : string; // 직원명
  position          : string; // 직급
  department        : string; // 부서
  hasHrEvaluation   : string; // 기본 인사평가 권한 여부 ('Y' | 'N') - 'N'인 경우 프로젝트평가만 진행
  period?           : string; // 평가 기간 구분 ('PLG' | 'HR')
  firstEvaluatorId? : string; // 1차 평가자 ID
  secondEvaluatorId?: string; // 2차 평가자 ID
  evaluations       : {
    bizPlgEvaluation    : string;              // 업무서약서 완료 여부 ('Y' | 'N')
    selfEvaluation      : string;              // 자기평가 완료 여부 ('Y' | 'N')
    firstEvaluation     : string;              // 1차평가 완료 여부 ('Y' | 'N')
    secondEvaluation    : string;              // 2차평가 완료 여부 ('Y' | 'N')
    allPjtEvaluations   : string;              // 모든 프로젝트 평가 완료 여부 ('Y' | 'N') - 1차평가 활성화 조건
    projectEvaluations  : ProjectEvaluation[]; // 프로젝트 평가 목록
  };
  evaluationPeriods : {
    bizPlgEvaluation?   : EvaluationPeriod;    // 업무서약서 평가 기간
    selfEvaluation?     : EvaluationPeriod;    // 자기평가 기간
    firstEvaluation?    : EvaluationPeriod;    // 1차평가 기간
    secondEvaluation?   : EvaluationPeriod;    // 2차평가 기간
  };
}

/**
 * 평가 파라미터 상태
 */
export interface IEvaluationParamsState {
  employeeId?    : string;         // 평가 대상 직원 ID
  evaluationType?: EvaluationType; // 평가 타입
  projectId?     : string;         // 프로젝트 ID (프로젝트평가인 경우)
}

/**
 * 조회 조건 상태
 */
export interface IInquiryCndsState {
  department?      : string; // 부서
  searchKeyword?   : string; // 검색 키워드
  evaluationStatus?: string; // 평가 상태 필터
}

/**
 * 현재 사용자 정보
 */
export interface ICurrentUserState {
  id  : string; // 사용자 ID
  name: string; // 사용자명
  role: string; // 사용자 역할
}

/**
 * EVL0101M 화면 전체 상태
 */
export interface IEvl0101MState {
  currentPage         : PageType;                       // 현재 페이지
  evaluationParams    : IEvaluationParamsState;         // 평가 파라미터
  employees           : Employee[];                     // 직원 목록
  currentUser         : ICurrentUserState;              // 현재 사용자 정보
  inquiryCnds         : IInquiryCndsState;              // 조회 조건
  evaluationCycles    : EvaluationCycle[];              // 평가 주기 데이터
  evaluationDeadlines : Record<EvaluationType, string>; // 평가별 마감일
  loading             : boolean;                        // 로딩 상태
  error               : string | null;                  // 에러 메시지
  activeTab           : "hr" | "project";               // 현재 활성화된 탭 (기본 인사평가 / 프로젝트평가)
  selectedProjectId   : string | null;                  // 선택된 프로젝트 ID
  selectedEmployeeId  : string | null;                  // 선택된 직원 ID
}

/**
 * 평가 타입별 한글 표시명
 */
export const EVALUATION_TYPES : Record<EvaluationType, string> = {
  "work-commitment"    : "업무서약서",
  "self-evaluation"    : "자기평가",
  "first-evaluation"   : "1차평가",
  "second-evaluation"  : "2차평가",
  "project-evaluation" : "프로젝트평가",
};

// ====================================
// 유틸리티 함수들
// ====================================

/**
 * 날짜에 1일 더하는 헬퍼 함수
 */
export const getNextDay = (dateStr : string) : string => {
  // 날짜가 없거나 유효하지 않으면 빈 문자열 반환
  if (!dateStr || dateStr.length < 10) return '';

  const date = new Date(dateStr);
  // Invalid Date 체크
  if (isNaN(date.getTime())) return '';

  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

/**
 * 연도별 평가 기간 정보 생성 함수
 */
export const getEvaluationPeriods = (cycle : EvaluationCycle) => {
  return {
    "work-commitment"   : {
      startDate         : cycle.bizPlgStDt || "",
      endDate           : cycle.bizPlgEdDt || "",
    },
    "self-evaluation"   : {
      startDate         : cycle.hrEvlStDt  || "",
      endDate           : cycle.slfEvlEdDt || "",
    },
    "first-evaluation"  : {
      startDate         : getNextDay(cycle.slfEvlEdDt),  // 자기평가 종료일 + 1일
      endDate           : cycle.eval1EdDt  || "",
    },
    "second-evaluation" : {
      startDate         : getNextDay(cycle.eval1EdDt),   // 1차평가 종료일 + 1일
      endDate           : cycle.eval2EdDt  || "",
    },
    "project-evaluation"                              : {
      startDate         : cycle.hrEvlStDt  || "",
      endDate           : cycle.pjtEvlEdDt || "",
    },
  };
};

/**
 * 연도별 평가 마감일 정보 생성 함수
 */
export const getEvaluationDeadlines = (cycle                              : EvaluationCycle) => {
  return {
    "work-commitment"    : cycle.bizPlgEdDt || "-",
    "self-evaluation"    : cycle.slfEvlEdDt || "-",
    "first-evaluation"   : cycle.eval1EdDt || "-",
    "second-evaluation"  : cycle.eval2EdDt || "-",
    "project-evaluation" : cycle.pjtEvlEdDt || "-",
  };
};